// What the Workspace screens reach besides Me's context: the host's pieces
// (sections, windows, live editing) and the workspace being shown.
import type { WorkspaceView } from "@trameria/cloud-api/workspace-shared";
import type { LiveTransport } from "@trameria/ui-core";
import { createContext, useContext } from "react";
import type { WorkspaceSection } from "./sections";
import type { Windows } from "./windows";

export interface WorkspaceHost {
  /** The registered sections, in order. */
  sections: readonly WorkspaceSection[];
  windows: Windows;
  /** How a Case opened beside a thing reaches live editing. */
  live: LiveTransport;
}

export const WorkspaceHostContext = createContext<WorkspaceHost | null>(null);

export function useWorkspaceHost(): WorkspaceHost {
  const host = useContext(WorkspaceHostContext);
  if (!host) throw new Error("useWorkspaceHost outside the workspace screens");
  return host;
}

/** How a part's note written live (an issue's) reaches live editing. */
export function useWorkspaceLive(): LiveTransport {
  return useWorkspaceHost().live;
}

export interface CurrentWorkspace {
  slug: string;
  view: WorkspaceView;
}

export const CurrentWorkspaceContext = createContext<CurrentWorkspace | null>(
  null,
);

/** The workspace of the route being shown (inside /<slug>/...). */
export function useWorkspace(): CurrentWorkspace {
  const current = useContext(CurrentWorkspaceContext);
  if (!current) throw new Error("useWorkspace outside a workspace");
  return current;
}

/** Whether I hold a permission here (the owner holds every one). */
export function useCan(): (permission: string) => boolean {
  const { view } = useWorkspace();
  return (permission) =>
    view.me.isOwner || view.me.permissions.includes(permission);
}
